import _ from "lodash";
import moment from "moment";

function makeTreeFromFlatList(input, indexer, maker, root = null) {
    const cache = new Map();
    // get or create (and return) children array for the passed key;
    const getChildrenArray = (key) => {
        return cache.get(key) || cache.set(key, []).get(key);
    };
    for (const item of input) {
        getChildrenArray(indexer(item)).push(maker(item, getChildrenArray));
    }
    return cache.get(root);
}
function makeFlatListFromTree(input, key) {
    return [];
}
function uniqueProps(data) {
    return _.flatMap(data, (item) => {
        return Object.keys(item);
    });
}

/**
 * Planned seconds for one pass through a scheduled run's route.
 *
 * Replaces the old sum of CycleStage.duration: a run's planned time is now the sum of
 * its steps' planned_duration, seeded from the part and overridable per run. Null when
 * no step carries a duration, which simply means there is no target.
 */
function routeDurationSec(job) {
    const operations = job?.operations || [];
    if (!operations.length) return null;
    const total = operations.reduce(
        (sum, op) => sum + (op.planned_duration || 0),
        0,
    );
    return total > 0 ? total : null;
}

/** "2 of 5 operations" for a scheduled run, or null when it has no route. */
function routeProgressLabel(job) {
    const operations = job?.operations || [];
    if (!operations.length) return null;
    // Status 2 is Completed; see JOB_SCHEDULE_OPERATION_STATUSES.
    const done = operations.filter((op) => op.status === 2).length;
    return `${done} of ${operations.length} operations`;
}

function parseRouteEntityId(routeId) {
    if (routeId == null) return null;

    const trimmed = String(routeId).trim();
    if (!/^\d+$/.test(trimmed)) return null;

    const numericId = Number(trimmed);
    return Number.isSafeInteger(numericId) ? numericId : null;
}

function buildListRoute(basePath, searchParams) {
    const query = searchParams?.toString?.() || "";
    return query ? `${basePath}?${query}` : basePath;
}

function shortDateString(value) {
    return moment(value).format("ddd D/M/Y");
}
function longDateString(value) {
    return moment(value).format("LLL");
}

function graphAxisDateString(value) {
    return moment(value).format("ddd, MMM D YYYY HH:mm");
}

function getSignalTypeFromBits(value, types) {
    const flags = types?.device?.flags;

    if (!flags) throw new Error("Flags not found in types response");

    const {
        BIT_LOCATION_ANALOG,
        BIT_LOCATION_DIGITAL,
        BIT_LOCATION_INPUT,
        BIT_LOCATION_OUTPUT,
        BIT_LOCATION_STATUS,
        BIT_LOCATION_CONTROL,
        BIT_LOCATION_LO_BUS,
        BIT_LOCATION_HI_BUS,
    } = flags;

    const isAnalog = isBitSet(value, BIT_LOCATION_ANALOG);
    const isDigital = isBitSet(value, BIT_LOCATION_DIGITAL);
    const isInput = isBitSet(value, BIT_LOCATION_INPUT);
    const isOutput = isBitSet(value, BIT_LOCATION_OUTPUT);
    const isStatus = isBitSet(value, BIT_LOCATION_STATUS);
    const isControl = isBitSet(value, BIT_LOCATION_CONTROL);

    // Extract bus type bits
    const loBusBit = isBitSet(value, BIT_LOCATION_LO_BUS) ? 1 : 0;
    const hiBusBit = isBitSet(value, BIT_LOCATION_HI_BUS) ? 1 : 0;
    const busBits = (hiBusBit << 1) | loBusBit;

    console.log("=== Bus Type Debug ===");
    console.log("Value:", value, "Binary:", value.toString(2).padStart(8, "0"));
    console.log(
        "BIT_LOCATION_LO_BUS:",
        BIT_LOCATION_LO_BUS,
        "BIT_LOCATION_HI_BUS:",
        BIT_LOCATION_HI_BUS,
    );
    console.log("loBusBit:", loBusBit, "hiBusBit:", hiBusBit);
    console.log("busBits:", busBits);
    console.log("=====================");

    const busFlags = {
        GPIO: busBits === 0,
        UART: busBits === 1,
        I2C: busBits === 2,
        VIRTUAL: busBits === 3,
    };

    return {
        analog: isAnalog,
        digital: isDigital,
        input: isInput,
        output: isOutput,
        control: isControl,
        status: isStatus,
        ...busFlags,
    };
}

function isBitSet(value, bitLocation) {
    const numericValue =
        typeof value === "number"
            ? value
            : Number.parseInt(String(value || ""), 10);

    const numericBit =
        typeof bitLocation === "number"
            ? bitLocation
            : Number.parseInt(String(bitLocation || ""), 10);

    if (!Number.isFinite(numericValue) || !Number.isFinite(numericBit)) {
        return false;
    }

    return (numericValue & (1 << numericBit)) !== 0;
}

function setBit(value, bitLocation) {
    const numericValue =
        typeof value === "number"
            ? value
            : Number.parseInt(String(value || "0"), 10);

    const numericBit =
        typeof bitLocation === "number"
            ? bitLocation
            : Number.parseInt(String(bitLocation || ""), 10);

    if (!Number.isFinite(numericValue) || !Number.isFinite(numericBit)) {
        return Number.isFinite(numericValue) ? numericValue : 0;
    }

    return numericValue | (1 << numericBit);
}

function clearBit(value, bitLocation) {
    const numericValue =
        typeof value === "number"
            ? value
            : Number.parseInt(String(value || "0"), 10);

    const numericBit =
        typeof bitLocation === "number"
            ? bitLocation
            : Number.parseInt(String(bitLocation || ""), 10);

    if (!Number.isFinite(numericValue) || !Number.isFinite(numericBit)) {
        return Number.isFinite(numericValue) ? numericValue : 0;
    }

    return numericValue & ~(1 << numericBit);
}

function toNumericBitLocation(value) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
}

function resolveEntityKindBitLocations(types) {
    const flags =
        types?.entity_type?.entity_kind_flags ||
        types?.entityType?.entity_kind_flags ||
        {};

    return {
        entity: toNumericBitLocation(flags.BIT_LOCATION_ENTITY) ?? 0,
        association:
            toNumericBitLocation(flags.BIT_LOCATION_ASSOC) ??
            toNumericBitLocation(flags.BIT_LOCATION_ASSOCIATION) ??
            1,
        action: toNumericBitLocation(flags.BIT_LOCATION_ACTION) ?? 2,
        notifier: toNumericBitLocation(flags.BIT_LOCATION_NOTIFIER) ?? 3,
    };
}

function shortDurationString(duration) {
    const seconds = duration % 60;
    const totalMinutes = Math.floor(duration / 60);
    const minutes = totalMinutes % 60;
    const totalHours = Math.floor(totalMinutes / 60);
    const hours = totalHours % 24;
    const totalDays = Math.floor(totalHours / 24);
    const days = totalDays % 7;
    const weeks = Math.floor(totalDays / 7) % 4; // Approximate 4 weeks per month
    const months = Math.floor(totalDays / 30) % 12; // Approximate 30 days per month
    const years = Math.floor(totalDays / 365);

    if (duration < 60) {
        return `${seconds}s`; // Less than a minute
    } else if (duration < 3600) {
        return `${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(
            2,
            "0",
        )}s`; // Less than an hour
    } else if (duration < 86400) {
        return `${String(hours).padStart(2, "0")}h ${String(minutes).padStart(
            2,
            "0",
        )}m ${String(seconds).padStart(2, "0")}s`; // Less than a day
    } else if (duration < 604800) {
        return `${totalDays}d ${String(hours).padStart(2, "0")}h ${String(
            minutes,
        ).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`; // Less than a week
    } else if (duration < 2592000) {
        return `${weeks}w ${days}d ${String(hours).padStart(2, "0")}h ${String(
            minutes,
        ).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`; // Less than a month
    } else if (duration < 31536000) {
        return `${months}mo ${weeks}w ${days}d ${String(hours).padStart(
            2,
            "0",
        )}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(
            2,
            "0",
        )}s`; // Less than a year
    } else {
        return `${years}y ${months}mo ${weeks}w ${days}d ${String(
            hours,
        ).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m ${String(
            seconds,
        ).padStart(2, "0")}s`; // More than a year
    }
}

function timeString(value) {
    return moment(value * 1000).format("HH:mm");
}
function msFromUnix(value) {
    return value * 1000;
}
function unixFromMs(value) {
    return value / 1000;
}
function clampedText(value, min, max, minString, maxString) {
    if (value === max) return maxString;
    if (value === min) return minString;
    return value;
}

function generateSubIntervals(
    startTime,
    endTime,
    splitInterval = "minute",
    quantum = 1,
) {
    const timeSeries = [];
    while (startTime.isSameOrBefore(endTime)) {
        timeSeries.push(startTime.add(quantum, splitInterval).clone());
    }
    return timeSeries;
}

function getPageSizes() {
    return [5, 10, 20];
}

function getPriorityColor(timeInSeconds) {
    const days = Math.floor(timeInSeconds / 86400);
    if (days < 1) return "tw-text-red-500 tw-bg-red-100";
    if (days < 7) return "tw-text-yellow-500 tw-bg-yellow-100";
    return "tw-text-green-500 tw-bg-green-100";
}

function formatTimeToNextMaintenance(timeInSeconds) {
    const days = Math.floor(timeInSeconds / 86400);
    const hours = Math.floor((timeInSeconds % 86400) / 3600);
    return `${days} days ${hours} hours to go`;
}

/**
 * Last-seen label plus badge colour. Green while the device is within its
 * heartbeat interval, yellow until it counts as unresponsive, red after. The
 * limits come from Settings (useHeartbeatThresholds); the defaults are the
 * seeded 60 s / 300 s.
 */
function getHeartbeatStatus(
    epoch,
    { activeSecs = 60, unresponsiveSecs = 300 } = {},
) {
    const lastSeen = moment(epoch);
    const now = moment();
    const diffInSeconds = now.diff(lastSeen, "seconds");

    let label;
    if (diffInSeconds < 60) {
        label = `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
    } else if (diffInSeconds < 3600) {
        const diffInMinutes = now.diff(lastSeen, "minutes");
        label = `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffInSeconds < 86400) {
        const diffInHours = now.diff(lastSeen, "hours");
        label = `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    } else {
        const diffInDays = now.diff(lastSeen, "days");
        label = `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    }

    const style =
        diffInSeconds < activeSecs
            ? "tw-text-green-500 tw-bg-green-100"
            : diffInSeconds < unresponsiveSecs
                ? "tw-text-yellow-500 tw-bg-yellow-100"
                : "tw-text-red-500 tw-bg-red-100";

    return { label, style };
}

const dynamicStyleCache = new Set();
let dynamicStyleTag = null;

function ensureDynamicStyleTag() {
    if (typeof document === "undefined") return null;
    if (dynamicStyleTag && dynamicStyleTag.sheet) return dynamicStyleTag;

    const existing = document.getElementById("starek-dynamic-styles");
    if (existing && existing.sheet) {
        dynamicStyleTag = existing;
        return dynamicStyleTag;
    }

    const tag = document.createElement("style");
    tag.id = "starek-dynamic-styles";
    document.head.appendChild(tag);
    dynamicStyleTag = tag;
    return dynamicStyleTag;
}

function normalizeHexColor(hex) {
    if (!hex) return "#3b82f6";
    const value = hex.startsWith("#") ? hex : `#${hex}`;
    if (value.length === 4) {
        return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
    }
    return value.toLowerCase();
}

function hexToRgb(hex) {
    const normalized = normalizeHexColor(hex).replace("#", "");
    const num = parseInt(normalized, 16);
    return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255,
    };
}

function rgbToHex(r, g, b) {
    const toHex = (value) => value.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToRgba(r, g, b, alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function shadeColor(hex, percent) {
    const { r, g, b } = hexToRgb(hex);
    const target = percent < 0 ? 0 : 255;
    const amount = Math.abs(percent);
    const nextR = Math.round((target - r) * amount + r);
    const nextG = Math.round((target - g) * amount + g);
    const nextB = Math.round((target - b) * amount + b);
    return rgbToHex(nextR, nextG, nextB);
}

function getLightColor(hex, percent = 0.9) {
    return shadeColor(hex, Math.min(Math.max(percent, 0), 1));
}

function getDarkColor(hex, percent = 0.2) {
    return shadeColor(hex, -Math.min(Math.max(percent, 0), 1));
}

// --- RGB to HSL conversion ---
function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    let h,
        s,
        l = (max + min) / 2;

    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = d / (1 - Math.abs(2 * l - 1));
        switch (max) {
            case r:
                h = 60 * (((g - b) / d) % 6);
                break;
            case g:
                h = 60 * ((b - r) / d + 2);
                break;
            case b:
                h = 60 * ((r - g) / d + 4);
                break;
        }
    }
    if (h < 0) h += 360;
    return { h, s, l };
}

// --- HSL to RGB conversion ---
function hslToRgb(h, s, l) {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r, g, b;
    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];

    return {
        r: Math.round((r + m) * 255),
        g: Math.round((g + m) * 255),
        b: Math.round((b + m) * 255),
    };
}

// --- Hex to RGB array format (for destructuring compatibility) ---
function hexToRgbArray(hex) {
    const normalized = normalizeHexColor(hex).replace("#", "");
    return [
        parseInt(normalized.substring(0, 2), 16),
        parseInt(normalized.substring(2, 4), 16),
        parseInt(normalized.substring(4, 6), 16),
    ];
}

// --- Color variant generation with HSL-based gradients ---
function getColorVariant(baseColor, percentage) {
    if (!baseColor) return "#000000";

    const hex = baseColor.startsWith("#") ? baseColor : "#" + baseColor;
    const [r, g, b] = hexToRgbArray(hex);
    const { h, s, l } = rgbToHsl(r, g, b);

    // Create a dynamic range with inverted mapping:
    // 0% -> very light (0.85), 100% -> very dark (0.15)
    // Also increase saturation for darker shades
    const percentageRatio = percentage / 100;

    // Inverted lightness range: from 0.85 (light) to 0.15 (dark)
    const minL = 0.15; // Dark for high percentages
    const maxL = 0.85; // Light for low percentages
    const finalL = maxL - percentageRatio * (maxL - minL); // Inverted: high % = dark

    // Increase saturation for higher percentages to make dark colors more saturated
    const adjustedS = s * (0.6 + percentageRatio * 0.4); // Increase sat by up to 40%

    const { r: newR, g: newG, b: newB } = hslToRgb(h, adjustedS, finalL);
    return rgbToHex(newR, newG, newB);
}

/**
 * Get signal color from device parameters or index-based fallback from types API
 * Color resolution priority:
 * 1. Device parameters: shadowDevice.parameters[signalKey].color
 * 2. Types API: defaultSignalColors[signalIndex]
 * 3. Fallback: #333333 (dark gray)
 * @param {Object} currentDevice - The device object
 * @param {string} signalKey - The signal key (e.g., '/node0/port0')
 * @param {number} signalIndex - The signal index for fallback
 * @param {Array} defaultSignalColors - Default colors from types API
 * @returns {string} Hex color string with # prefix
 */
function getSignalColorFromDevice(
    currentDevice,
    signalKey,
    signalIndex,
    defaultSignalColors,
) {
    // First, try to get color from device parameters
    if (currentDevice) {
        let params = currentDevice.parameters;
        if (typeof params === "string") {
            try {
                params = JSON.parse(params);
            } catch (e) {
                console.error(
                    "Failed to parse parameters for device",
                    currentDevice.name,
                    e,
                );
                params = {};
            }
        }

        const signalParams = params?.[signalKey];
        if (signalParams?.color) {
            const color = signalParams.color;
            return color.startsWith("#") ? color : `#${color}`;
        }
    }

    // Fallback to index-based color from types API
    const fallbackColor =
        defaultSignalColors?.[signalIndex % defaultSignalColors.length];
    if (fallbackColor) {
        return fallbackColor.startsWith("#")
            ? fallbackColor
            : `#${fallbackColor}`;
    }
    return "#333333";
}

/**
 * Get a color gamut with background (20% opacity) and border (100%) colors
 * @param {string} baseColor - Hex color (e.g., '#3b82f6')
 * @returns {Object} {background: 'rgba(...)', border: '#...'} with 20% and 100% opacity variants
 */
function getColorGamut(baseColor) {
    const normalized = normalizeHexColor(baseColor);
    const { r, g, b } = hexToRgb(normalized);

    return {
        background: rgbToRgba(r, g, b, 0.2), // 20% opacity for light background
        border: normalized, // 100% opacity for border
    };
}

function registerDynamicClass(className, rules) {
    if (dynamicStyleCache.has(className)) return;
    const tag = ensureDynamicStyleTag();
    if (!tag || !tag.sheet) return;
    try {
        tag.sheet.insertRule(
            `.${className} { ${rules} }`,
            tag.sheet.cssRules.length,
        );
        dynamicStyleCache.add(className);
    } catch (err) {
        console.warn("Failed to register dynamic style", className, err);
    }
}

function getSignalCardClass(baseColor) {
    const normalized = normalizeHexColor(baseColor);
    const gamut = getColorGamut(normalized);
    const className = `sig-card-${normalized.replace("#", "")}`;
    registerDynamicClass(
        className,
        `background-color: ${gamut.background}; border-color: ${gamut.border};`,
    );
    return className;
}

function getSignalBarClass(baseColor) {
    const normalized = normalizeHexColor(baseColor);
    const className = `sig-bar-${normalized.replace("#", "")}`;
    registerDynamicClass(className, `background-color: ${normalized};`);
    return className;
}

function getProgressWidthClass(percent) {
    const clamped = Math.max(0, Math.min(100, Math.round(percent)));
    const className = `progress-w-${clamped}`;
    registerDynamicClass(className, `width: ${clamped}%;`);
    return className;
}

function getSparklineHeightClass(percent) {
    const clamped = Math.max(0, Math.min(100, Math.round(percent)));
    const className = `spark-h-${clamped}`;
    registerDynamicClass(className, `height: ${clamped}%;`);
    return className;
}

function printReports(element) {
    if (!element) return;

    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) {
        alert("Please allow popups to print");
        return;
    }

    const doc = printWindow.document;
    doc.open();
    doc.write("<html><head><title>Print Report</title>");

    const styles = document.querySelectorAll('style, link[rel="stylesheet"]');
    styles.forEach((style) => {
        doc.write(style.outerHTML);
    });

    doc.write(
        "<style>body { margin: 0; padding: 20px; background-color: white; } .tw-h-full { height: auto !important; } .tw-overflow-y-auto { overflow: visible !important; height: auto !important; }</style>",
    );

    doc.write("</head><body>");
    doc.write(element.outerHTML);
    doc.write("</body></html>");
    doc.close();

    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
    };

    setTimeout(() => {
        printWindow.focus();
        printWindow.print();
    }, 500);
}

export {
    makeFlatListFromTree,
    makeTreeFromFlatList,
    uniqueProps,
    parseRouteEntityId,
    buildListRoute,
    routeDurationSec,
    routeProgressLabel,
    shortDateString,
    longDateString,
    graphAxisDateString,
    shortDurationString,
    timeString,
    msFromUnix,
    unixFromMs,
    clampedText,
    generateSubIntervals,
    getPageSizes,
    getSignalTypeFromBits,
    isBitSet,
    setBit,
    clearBit,
    resolveEntityKindBitLocations,
    getGpioLabelOptionsFromType,
    getPriorityColor,
    formatTimeToNextMaintenance,
    getHeartbeatStatus,
    printReports,
    truncateString,
    getLightColor,
    getDarkColor,
    rgbToHsl,
    hslToRgb,
    hexToRgbArray,
    getColorVariant,
    getSignalColorFromDevice,
    getColorGamut,
    getSignalCardClass,
    getSignalBarClass,
    getProgressWidthClass,
    getSparklineHeightClass,
    normalizePhoneNumber,
    isValidPhoneNumber,
    DEFAULT_COUNTRY_CODE,
    PHONE_COUNTRY_CODES,
    isValidNationalPhone,
    splitPhoneNumber,
};

// E.164: leading "+", country code starting 1-9, up to 15 digits total.
const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

// Accepts "+1 (415) 555-2671" and stores it as "+14155552671".
// A leading "00" international prefix is rewritten to "+".
// Returns null for empty input (clearing the number).
function normalizePhoneNumber(raw) {
    const trimmed = String(raw ?? "").trim();
    if (!trimmed) return null;

    let compact = trimmed.replace(/[\s().-]/g, "");
    if (compact.startsWith("00")) compact = `+${compact.slice(2)}`;

    return compact;
}

function isValidPhoneNumber(value) {
    return E164_PATTERN.test(value);
}

// Phone inputs take the country code and the 10-digit national number separately,
// then join them as "+<code><number>" so the backend's E.164 check still passes.
const DEFAULT_COUNTRY_CODE = "+91";
// Only codes whose national numbers are 10 digits, to match isValidNationalPhone.
const PHONE_COUNTRY_CODES = ["+91", "+1", "+44"];
const NATIONAL_PHONE_PATTERN = /^\d{10}$/;

function isValidNationalPhone(digits) {
    return NATIONAL_PHONE_PATTERN.test(String(digits ?? ""));
}

// "+14155552671" -> { countryCode: "+1", number: "4155552671" }.
// A known country code is matched first; otherwise the national number is the
// last 10 digits and anything before it is the country code.
function splitPhoneNumber(e164) {
    const compact = normalizePhoneNumber(e164);
    if (!compact) return { countryCode: DEFAULT_COUNTRY_CODE, number: "" };

    const digits = compact.replace(/\D/g, "");
    const known = compact.startsWith("+")
        ? [...PHONE_COUNTRY_CODES]
              .sort((x, y) => y.length - x.length)
              .find((code) => compact.startsWith(code))
        : null;
    if (known) {
        return { countryCode: known, number: digits.slice(known.length - 1) };
    }
    if (!compact.startsWith("+") || digits.length <= 10) {
        return { countryCode: DEFAULT_COUNTRY_CODE, number: digits.slice(-10) };
    }
    return {
        countryCode: `+${digits.slice(0, -10)}`,
        number: digits.slice(-10),
    };
}

function truncateString(str, maxLength) {
    if (!str) return "";
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + "...";
}

// Helper function to get GPIO label options from type bitmap without API dependency
function getGpioLabelOptionsFromType(channelType) {
    // Bit flags based on SIGNAL_TYPE_BITMAP
    // D0: O (Output), D1: I (Input), D2: D (Discrete), D3: A (Analog), D5: C (Control)
    const isAnalog = isBitSet(channelType, 3); // Bit 3: A (Analog)
    const isDiscrete = isBitSet(channelType, 2); // Bit 2: D (Discrete)
    const isInput = isBitSet(channelType, 1); // Bit 1: I (Input) -> Sense
    const isOutput = isBitSet(channelType, 0); // Bit 0: O (Output) -> Actuate
    const isControl = isBitSet(channelType, 5); // Bit 5: C (Control)

    // Always provide all options, but determine which should be selected based on type
    const options1 = ["Discrete", "Proportional"];
    const selectedLabel1 = isAnalog ? "Proportional" : "Discrete";

    // Determine Label 2 options - always provide both Sense and Actuate
    const options2 = ["Sense", "Actuate"];
    const selectedLabel2 = isOutput ? "Actuate" : "Sense";

    // Determine Label 3 options (Control vs State)
    const options3 = ["Control", "State"];
    const selectedLabel3 = isControl ? "Control" : "State";

    return {
        options1,
        selectedLabel1,
        options2,
        selectedLabel2,
        options3,
        selectedLabel3,
    };
}
